import { keepPreviousData } from '@tanstack/react-query'
import type { ColDef, ICellRendererParams } from 'ag-grid-community'
import type { CustomInnerHeaderProps } from 'ag-grid-react'
import { ShoppingCart, Package, Hash, Info } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'

import type { CategoryPairItem, ProductPairItem } from '@/api/admin-analytics-types'
import { useGetBasketAnalysis, useListProductCategories } from '@/api/react-query'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/base/card'
import { Combobox } from '@/components/base/combobox'
import { Skeleton } from '@/components/base/skeleton'
import { Text } from '@/components/base/text'
import { Thumbnail } from '@/components/base/thumbnail'
import { Tooltip } from '@/components/base/tooltip'
import { Pagination } from '@/components/table/pagination'
import { Table, type ServerSortConfig, type SortDirection } from '@/components/table/table'
import { useI18n } from '@/i18n/i18n-react'
import { useDebounce } from '@/utils/hooks'

import { KPICard, ProductMultiSelect, useComparisonLabel } from './analytics-shared'

/** Rows shown per page in the Product Pairs / Category Affinity tables (client-side, in-memory). */
const PAGE_SIZE = 10

/** Metric columns the basket pair tables can be sorted by (server-side, before the response cap). */
export type BasketPairSortField = 'coOccurrenceCount' | 'support' | 'confidence' | 'lift'

/** Column ids the backing API can sort by; other columns render non-sortable. */
const SORTABLE_PAIR_COLUMNS: BasketPairSortField[] = ['coOccurrenceCount', 'support', 'confidence', 'lift']

type AnalyticsInterval = 'day' | 'week' | 'month' | 'quarter' | 'year'

/** Column header label with an info icon whose tooltip explains the metric in plain language. */
function MetricHeader({ displayName, tooltip }: CustomInnerHeaderProps & { tooltip: string }) {
	return (
		<div className='flex items-center gap-1 overflow-hidden'>
			<span className='truncate'>{displayName}</span>
			<Tooltip content={tooltip} maxWidth={280}>
				<span className='flex shrink-0 items-center text-ui-fg-muted hover:text-ui-fg-subtle'>
					<Info className='size-3.5' />
				</span>
			</Tooltip>
		</div>
	)
}

const metricHeaderParams = (tooltip: string) => ({
	innerHeaderComponent: MetricHeader,
	innerHeaderComponentParams: { tooltip }
})

function ProductCell({ title, imageUrl }: { title: string; imageUrl: string | null }) {
	return (
		<div className='flex items-center gap-2 py-1'>
			<Thumbnail src={imageUrl} size='small' />
			<Text className='truncate'>{title}</Text>
		</div>
	)
}

/** Category multi-select above the Category Affinity table (server-side searchable), used for both
 *  the anchor filter and the exclusion filter (the placeholder distinguishes them). */
function CategoryAnchorFilter({
	value,
	onChange,
	placeholder
}: {
	value: string[]
	onChange: (uuids: string[]) => void
	placeholder: string
}) {
	const [search, setSearch] = useState('')
	const debouncedSearch = useDebounce(search)
	const categoriesQuery = useListProductCategories({ limit: 100, q: debouncedSearch || undefined })

	const options = useMemo(
		() => (categoriesQuery.data?.categories ?? []).map(c => ({ value: c.uuid, label: c.name })),
		[categoriesQuery.data]
	)

	return (
		<Combobox
			className='w-full sm:w-[16rem]'
			value={value}
			onChange={next => onChange((next as string[]) ?? [])}
			options={options}
			optionsLoading={categoriesQuery.isLoading}
			onSearchValueChange={setSearch}
			placeholder={placeholder}
		/>
	)
}

export function BasketAnalyticsView({
	interval,
	periodStart,
	productUuids,
	categoryUuids,
	excludeProductUuids,
	excludeCategoryUuids,
	salesChannelUuids,
	minSupport,
	productSortBy,
	productSortOrder,
	categorySortBy,
	categorySortOrder,
	onProductUuidsChange,
	onCategoryUuidsChange,
	onExcludeProductUuidsChange,
	onExcludeCategoryUuidsChange,
	onProductSortChange,
	onCategorySortChange
}: {
	interval: AnalyticsInterval
	periodStart: string | undefined
	productUuids: string[] | undefined
	categoryUuids: string[] | undefined
	excludeProductUuids: string[] | undefined
	excludeCategoryUuids: string[] | undefined
	salesChannelUuids: string[] | undefined
	minSupport: number
	productSortBy: BasketPairSortField | undefined
	productSortOrder: SortDirection | undefined
	categorySortBy: BasketPairSortField | undefined
	categorySortOrder: SortDirection | undefined
	onProductUuidsChange: (uuids: string[]) => void
	onCategoryUuidsChange: (uuids: string[]) => void
	onExcludeProductUuidsChange: (uuids: string[]) => void
	onExcludeCategoryUuidsChange: (uuids: string[]) => void
	onProductSortChange: (sortBy: BasketPairSortField | undefined, sortOrder: SortDirection | undefined) => void
	onCategorySortChange: (sortBy: BasketPairSortField | undefined, sortOrder: SortDirection | undefined) => void
}) {
	const { L } = useI18n()
	const BA = L.Analytics.BasketAnalysis

	const { data, isLoading } = useGetBasketAnalysis(
		{
			interval,
			...(periodStart ? { periodStart } : {}),
			productUuids,
			categoryUuids,
			excludeProductUuids,
			excludeCategoryUuids,
			salesChannelUuids,
			minSupport,
			// Server-side sort (applied before the response cap) so the returned top-N reflects the
			// chosen metric rather than reordering only the current page.
			...(productSortBy ? { productSortBy, productSortOrder } : {}),
			...(categorySortBy ? { categorySortBy, categorySortOrder } : {})
		},
		// Keep the previous results visible while a new (often slow, live-computed) filtered request
		// runs, so the KPIs and tables don't blank out into skeletons on every filter change.
		{ placeholderData: keepPreviousData }
	)

	const overview = data?.overview
	const comparisonLabel = useComparisonLabel(overview?.comparisonRange)
	const productPairs = data?.productPairs ?? []
	const categoryPairs = data?.categoryPairs ?? []

	// Client-side pagination over the (already-capped) in-memory pair lists. Snap back to the first
	// page whenever the list changes (anchor filter, min support, channel, period, or sort) so we
	// never linger on a now-empty page.
	const [productOffset, setProductOffset] = useState(0)
	const [categoryOffset, setCategoryOffset] = useState(0)
	useEffect(() => {
		setProductOffset(0)
	}, [productSortBy, productSortOrder])
	useEffect(() => {
		setCategoryOffset(0)
	}, [categorySortBy, categorySortOrder])
	useEffect(() => {
		if (productOffset >= productPairs.length) setProductOffset(0)
	}, [productOffset, productPairs.length])
	useEffect(() => {
		if (categoryOffset >= categoryPairs.length) setCategoryOffset(0)
	}, [categoryOffset, categoryPairs.length])
	const productPairsPage = productPairs.slice(productOffset, productOffset + PAGE_SIZE)
	const categoryPairsPage = categoryPairs.slice(categoryOffset, categoryOffset + PAGE_SIZE)

	// Reflect the active sort in each table header, restrict sortable columns to those the API can
	// order by, and push the chosen column/direction back up to the URL (which re-queries).
	const productServerSort: ServerSortConfig = {
		sortBy: productSortBy,
		sortOrder: productSortOrder,
		sortableColumns: SORTABLE_PAIR_COLUMNS,
		onChange: ({ sortBy, sortOrder }) => onProductSortChange(sortBy as BasketPairSortField | undefined, sortOrder)
	}
	const categoryServerSort: ServerSortConfig = {
		sortBy: categorySortBy,
		sortOrder: categorySortOrder,
		sortableColumns: SORTABLE_PAIR_COLUMNS,
		onChange: ({ sortBy, sortOrder }) => onCategorySortChange(sortBy as BasketPairSortField | undefined, sortOrder)
	}

	const productPairColumns = useMemo<ColDef<ProductPairItem>[]>(
		() => [
			{
				headerName: BA.ProductA(),
				field: 'productATitle',
				flex: 1,
				minWidth: 180,
				cellRenderer: (params: ICellRendererParams<ProductPairItem>) =>
					params.data ? (
						<ProductCell title={params.data.productATitle} imageUrl={params.data.productAImageUrl} />
					) : null
			},
			{
				headerName: BA.ProductB(),
				field: 'productBTitle',
				flex: 1,
				minWidth: 180,
				cellRenderer: (params: ICellRendererParams<ProductPairItem>) =>
					params.data ? (
						<ProductCell title={params.data.productBTitle} imageUrl={params.data.productBImageUrl} />
					) : null
			},
			{
				headerName: BA.CoOccurrence(),
				field: 'coOccurrenceCount',
				width: 130,
				type: 'numericColumn'
			},
			{
				headerName: BA.Support(),
				field: 'support',
				width: 130,
				headerComponentParams: metricHeaderParams(BA.SupportTooltip()),
				valueFormatter: params => BA.MetricPercent(params.value?.toFixed(1) ?? '0')
			},
			{
				headerName: BA.Confidence(),
				field: 'confidence',
				width: 145,
				headerComponentParams: metricHeaderParams(BA.ConfidenceTooltip()),
				valueFormatter: params => BA.MetricPercent(params.value?.toFixed(1) ?? '0')
			},
			{
				headerName: BA.Lift(),
				field: 'lift',
				width: 105,
				headerComponentParams: metricHeaderParams(BA.LiftTooltip()),
				valueFormatter: params => params.value?.toFixed(2) ?? '0'
			}
		],
		[BA]
	)

	const categoryPairColumns = useMemo<ColDef<CategoryPairItem>[]>(
		() => [
			{ headerName: BA.CategoryA(), field: 'categoryAName', flex: 1, minWidth: 160 },
			{ headerName: BA.CategoryB(), field: 'categoryBName', flex: 1, minWidth: 160 },
			{
				headerName: BA.CoOccurrence(),
				field: 'coOccurrenceCount',
				width: 130,
				type: 'numericColumn'
			},
			{
				headerName: BA.Support(),
				field: 'support',
				width: 130,
				headerComponentParams: metricHeaderParams(BA.SupportTooltip()),
				valueFormatter: params => BA.MetricPercent(params.value?.toFixed(1) ?? '0')
			},
			{
				headerName: BA.Confidence(),
				field: 'confidence',
				width: 145,
				headerComponentParams: metricHeaderParams(BA.ConfidenceTooltip()),
				valueFormatter: params => BA.MetricPercent(params.value?.toFixed(1) ?? '0')
			},
			{
				headerName: BA.Lift(),
				field: 'lift',
				width: 105,
				headerComponentParams: metricHeaderParams(BA.LiftTooltip()),
				valueFormatter: params => params.value?.toFixed(2) ?? '0'
			}
		],
		[BA]
	)

	const emptyProductPairsMessage = (() => {
		if (productUuids?.length) return BA.NoAnchorPairs()
		if (productPairs.length === 0) return BA.NoProductPairs()
		return undefined
	})()

	return (
		<div className='space-y-6'>
			{isLoading ? (
				<div className='grid gap-4 md:grid-cols-3'>
					{[...Array(3)].map((_, i) => (
						<Skeleton key={i} className='h-32' />
					))}
				</div>
			) : overview ? (
				<div className='grid gap-4 md:grid-cols-3'>
					<KPICard
						title={BA.AvgItemsPerOrder()}
						current={overview.currentPeriod.avgItemsPerOrder}
						previous={overview.previousPeriod.avgItemsPerOrder}
						icon={Package}
						formatter={val => val.toFixed(1)}
						comparisonLabel={comparisonLabel}
					/>
					<KPICard
						title={BA.AvgUniqueProductsPerOrder()}
						current={overview.currentPeriod.avgUniqueProductsPerOrder}
						previous={overview.previousPeriod.avgUniqueProductsPerOrder}
						icon={Hash}
						formatter={val => val.toFixed(1)}
						comparisonLabel={comparisonLabel}
					/>
					<KPICard
						title={BA.OrdersAnalyzed()}
						current={overview.currentPeriod.orderCount}
						previous={overview.previousPeriod.orderCount}
						icon={ShoppingCart}
						comparisonLabel={comparisonLabel}
					/>
				</div>
			) : null}

			<Card>
				<CardHeader className='flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center'>
					<CardTitle>
						<Text>{BA.ProductPairs()}</Text>
					</CardTitle>
					<div className='flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center'>
						<ProductMultiSelect
							value={productUuids ?? []}
							onChange={onProductUuidsChange}
							placeholder={BA.ProductsPlaceholder()}
						/>
						<ProductMultiSelect
							value={excludeProductUuids ?? []}
							onChange={onExcludeProductUuidsChange}
							placeholder={BA.ExcludeProductsPlaceholder()}
						/>
					</div>
				</CardHeader>
				<CardContent className='p-0'>
					{isLoading ? (
						<Skeleton className='m-4 h-[360px]' />
					) : (
						<>
							<Table
								data={productPairsPage}
								columnDefs={productPairColumns}
								serverSort={productServerSort}
								getRowId={row => `${row.productAUuid}-${row.productBUuid}`}
								domLayout='autoHeight'
								emptyState={
									<div className='text-muted-foreground flex items-center justify-center p-8'>
										<Text>{emptyProductPairsMessage ?? L.Analytics.NoData()}</Text>
									</div>
								}
							/>
							{productPairs.length > PAGE_SIZE ? (
								<div className='px-3 py-2 sm:px-6 sm:py-4'>
									<Pagination
										totalCount={productPairs.length}
										limit={PAGE_SIZE}
										offset={productOffset}
										onOffsetChange={setProductOffset}
									/>
								</div>
							) : null}
						</>
					)}
				</CardContent>
			</Card>

			<Card>
				<CardHeader className='flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center'>
					<CardTitle>
						<Text>{BA.CategoryAffinity()}</Text>
					</CardTitle>
					<div className='flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center'>
						<CategoryAnchorFilter
							value={categoryUuids ?? []}
							onChange={onCategoryUuidsChange}
							placeholder={BA.CategoriesPlaceholder()}
						/>
						<CategoryAnchorFilter
							value={excludeCategoryUuids ?? []}
							onChange={onExcludeCategoryUuidsChange}
							placeholder={BA.ExcludeCategoriesPlaceholder()}
						/>
					</div>
				</CardHeader>
				<CardContent className='p-0'>
					{isLoading ? (
						<Skeleton className='m-4 h-[280px]' />
					) : (
						<>
							<Table
								data={categoryPairsPage}
								columnDefs={categoryPairColumns}
								serverSort={categoryServerSort}
								getRowId={row => `${row.categoryAUuid ?? 'null'}-${row.categoryBUuid ?? 'null'}`}
								domLayout='autoHeight'
								emptyState={
									<div className='text-muted-foreground flex items-center justify-center p-8'>
										<Text>{BA.NoCategoryPairs()}</Text>
									</div>
								}
							/>
							{categoryPairs.length > PAGE_SIZE ? (
								<div className='px-3 py-2 sm:px-6 sm:py-4'>
									<Pagination
										totalCount={categoryPairs.length}
										limit={PAGE_SIZE}
										offset={categoryOffset}
										onOffsetChange={setCategoryOffset}
									/>
								</div>
							) : null}
						</>
					)}
				</CardContent>
			</Card>
		</div>
	)
}
